import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {
  addToast,
  Anchor,
  Button,
  Checkbox,
  copyToClipboard,
  Dropdown,
  DropdownItem,
  Icon,
  IconButton,
  LoadingSpinner,
  MandarkTable,
  NoDataMsg,
  Wall
} from '@albert-io/atomic';
import {history} from 'client/history';
import constants from 'client/constants';
import {List} from 'immutable';
import {resource} from '@albert-io/json-api-framework/request/builder';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {renderMoment} from 'lib/timeUtils';
import RenameFolderModal from 'client/Folders/RenameFolderModal/RenameFolderModal.react';
import DeleteFolderModal from 'client/Folders/DeleteFolderModal/DeleteFolderModal.react';
import NewFolderModal from 'components/PracticeView/FoldersModal/NewFolderModal/NewFolderModal.react';
import AssignModal, {SmartAssignTooltip} from 'components/AssignModal/AssignModal.react';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import {useTracking} from 'react-tracking';
import {showPublicViewForSubject} from 'components/PracticeView/practiceView.utils';
import sessionStore from 'client/Session/SessionStore';
import UpgradeSubjectModal from 'components/PracticeView/UpgradeSubjectModal/UpgradeSubjectModal.react';

import './folders.scss';
import DuplicateFolderModal from '../DuplicateFolderModal/DuplicateFolderModal.react';
import EmptyFolderModal from '../EmptyFolderModal/EmptyFolderModal.react';

import cactusOfEmptiness from '../../Classrooms/Teacher/EmptyStateCta/desert_cactus.svg';

const FoldersEmptyState = ({setShowNewFolderModal, showNewFolderModal}) => {
  return (
    <NoDataMsg.Container centered className='folders-list--empty'>
      <img className='u-mar-b_4' src={cactusOfEmptiness} alt='' />
      <NoDataMsg.Heading>No folders yet</NoDataMsg.Heading>
      <NoDataMsg.Body className='u-mar-b_4'>
        Folders are customized lists of questions that you can create assignments with. Create a new
        folder by adding it from a subject guide or a question list, or by clicking the button
        below.
      </NoDataMsg.Body>
      <Button onClick={() => setShowNewFolderModal(true)}>Create a folder</Button>
      {showNewFolderModal && <NewFolderModal handleClose={() => setShowNewFolderModal(false)} />}
      <Anchor
        className='folders-list--empty__no-data'
        href='https://help.albert.io/en/articles/2829365-how-do-i-create-and-assign-a-folder'
        rel='noopener noreferrer'
        target='_blank'
      >
        Or learn more about creating folders
      </Anchor>
    </NoDataMsg.Container>
  );
};

FoldersEmptyState.propTypes = {
  setShowNewFolderModal: PropTypes.func,
  showNewFolderModal: PropTypes.bool
};

const Folders = () => {
  const {trackEvent} = useTracking({
    event: 'click',
    feature: 'Folders list'
  });

  const [folderToRename, setFolderToRename] = useState(null);
  const [folderToDuplicate, setFolderToDuplicate] = useState(null);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [showEmptyFolderModal, setShowEmptyFolderModal] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [columnsToShow, setColumnsToShow] = useState([
    'name',
    'created',
    'subjects',
    'modified',
    'questions'
  ]);

  const [folderToSmartAssign, setFolderToSmartAssign] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [blockedSubject, setBlockedSubject] = useState(null);

  const shareFolder = (folder) => {
    const folderId = folder.getId();
    copyToClipboard(`${constants.ALBERT_FULL_PATH}/folder/${folderId}`, () => {
      addToast({
        message:
          'The link to your folder has been copied to your clipboard. Share the link with other teachers to let them copy your folder.',
        title: 'Link Copied!',
        type: 'success'
      });
    });
  };

  const handleRename = (folder) => {
    setFolderToRename(folder);
  };

  const toggleEmptyFolderModal = () => {
    setShowEmptyFolderModal((prevShow) => !prevShow);
  };

  const makeDropdownButton = (folder) => {
    let dropdownItems = List([
      <DropdownItem
        as='button'
        icon={['far', 'i-cursor']}
        onClick={() => {
          trackEvent({component: 'Kabab menu', content: 'Rename', type: 'dropdown item'});
          handleRename(folder);
        }}
      >
        Rename
      </DropdownItem>,
      <DropdownItem
        as='button'
        icon={['far', 'copy']}
        onClick={() => {
          trackEvent({component: 'Kabab menu', content: 'Duplicate', type: 'dropdown item'});
          setFolderToDuplicate(folder);
        }}
      >
        Duplicate
      </DropdownItem>,
      <DropdownItem
        as='button'
        color='red'
        iconColor='destructive'
        icon={['far', 'trash-alt']}
        onClick={() => {
          trackEvent({component: 'Kabab menu', content: 'Delete', type: 'dropdown item'});
          setFolderToDelete(folder);
        }}
      >
        Delete
      </DropdownItem>
    ]);

    if (copyToClipboard.isSupported()) {
      dropdownItems = dropdownItems.insert(
        2,
        <DropdownItem
          as='button'
          icon={['far', 'link']}
          onClick={() => {
            trackEvent({component: 'Kabab menu', content: 'Copy Link', type: 'dropdown item'});
            shareFolder(folder);
          }}
        >
          Copy Link
        </DropdownItem>
      );
    }

    const countOfQuestions = folder.getMeta().getCountOfQuestions();
    const minSizeQuestionSet = folder.getMeta().getMinSizeQuestionSet();

    dropdownItems = dropdownItems.insert(0, [
      <DropdownItem
        as='button'
        icon={['far', 'folder-open']}
        onClick={() => {
          trackEvent({component: 'Kabab menu', content: 'Open', type: 'dropdown item'});
          if (countOfQuestions === 0) {
            toggleEmptyFolderModal();
          } else {
            history.pushState(null, `/folder/${folder.getId()}`);
          }
        }}
      >
        Open
      </DropdownItem>,
      <DropdownItem
        as='button'
        className='u-white-space_nowrap'
        icon={['far', 'file-plus']}
        onClick={() => {
          trackEvent({component: 'Kabab menu', content: 'Assign all', type: 'dropdown item'});
          if (countOfQuestions === 0) {
            toggleEmptyFolderModal();
          } else {
            history.pushState(null, `/create-assignment/folder/${folder.getId()}/`);
          }
        }}
      >
        Assign all
      </DropdownItem>,
      <DropdownItem
        as='button'
        onClick={() => {
          trackEvent({component: 'Kabab menu', content: 'Smart assign', type: 'dropdown item'});
          if (countOfQuestions === 0) {
            toggleEmptyFolderModal();
          } else if (countOfQuestions === 1) {
            history.pushState(null, `/create-assignment/folder/${folder.getId()}/`);
          } else if (countOfQuestions === minSizeQuestionSet) {
            history.pushState(null, `/create-assignment/folder/${folder.getId()}/`);
          } else {
            setFolderToSmartAssign(folder);
          }
        }}
      >
        <SmartAssignTooltip leftContent='Smart assign' />
      </DropdownItem>
    ]);

    return (
      <Dropdown
        position='right-start'
        trigger={
          <IconButton
            icon='ellipsis-v'
            size='s'
            label='dropdown'
            onClick={() => {
              trackEvent({
                type: 'dropdown',
                content: null,
                component: 'Folder row'
              });
            }}
          />
        }
      >
        {dropdownItems.map((item) => item)}
      </Dropdown>
    );
  };

  const renderSmartAssignModal = () => {
    return (
      folderToSmartAssign && (
        <AssignModal
          resource={folderToSmartAssign}
          type='folder'
          onClose={() => {
            setFolderToSmartAssign(null);
          }}
        />
      )
    );
  };

  const getFolderQuery = (withIncludesAndMeta = true) => {
    return (
      resource('template_v1')
        .mandarkEndpoint(['templates_v1'])
        .filter({
          teacher_v1: masqueradeStore.getUserIdByMasqueradeState()
        })
        /**
         * We don't need meta or includes to determine if the user
         * has any folders.
         *
         * @see L48
         */
        .withMeta('template_v1,subject_v2', withIncludesAndMeta)
        .customQuery({
          meta: {
            context: {
              student: {
                id: sessionStore.getUserId()
              }
            }
          }
        })
        .include('question_sets_v1.subject_v2', withIncludesAndMeta)
    );
  };

  const getSearchFilters = (query, term) => {
    return query.filter({
      name: {
        case_insensitive_substring: term
      }
    });
  };

  const handleColumnSelection = (event, column) => {
    const {checked} = event.target;

    if (checked) {
      setColumnsToShow((prevColumns) => [...prevColumns, column]);
    } else {
      setColumnsToShow((prevColumns) => prevColumns.filter((col) => col !== column));
    }
  };

  const {
    isReady,
    resources: [folderRecords]
  } = useMandarkQueries([getFolderQuery()]);

  const folders = isReady ? [...folderRecords] : [];

  if (!isReady) {
    return <LoadingSpinner size={3} />;
  }
  return (
    <>
      {!folders.length && (
        <FoldersEmptyState
          setShowNewFolderModal={setShowNewFolderModal}
          showNewFolderModal={showNewFolderModal}
        />
      )}
      {folders.length > 0 && (
        <div className='folders'>
          <Wall title={<Wall.Title as='h1'>Folders</Wall.Title>} />
          <div className='folders__wrapper'>
            <MandarkTable
              defaultSortDirection='DESCENDING'
              rowOnClickFunc={(folder) => {
                const subjects = folder
                  .getQuestionSets()
                  .map((qs) => {
                    return qs.getSubject();
                  })
                  .toSet();
                const blockedSubjects = [...subjects].filter((s) => showPublicViewForSubject(s));
                if (blockedSubjects.length > 0) {
                  setBlockedSubject(blockedSubjects[0]);
                  setShowUpgradeModal(true);
                } else if (folder.getMeta().getCountOfQuestions() !== 0) {
                  history.push(`/folder/${folder.getId()}`);
                }
              }}
              theme='list'
              electiveMobileSearch
              buttonGroup={[
                <Button onClick={() => setShowNewFolderModal(true)} label='newFolder'>
                  New folder
                </Button>,
                <Dropdown
                  trigger={
                    <Button color='secondary' variant='text' label='settings'>
                      Options
                      <Icon icon='caret-down' className='u-mar-l_1' />
                    </Button>
                  }
                >
                  <DropdownItem>
                    <Checkbox
                      className='u-mar-r_2'
                      key={columnsToShow.length}
                      onChange={(event) => handleColumnSelection(event, 'subjects')}
                      checked={columnsToShow.includes('subjects')}
                    />
                    Show Subjects
                  </DropdownItem>
                  <DropdownItem>
                    <Checkbox
                      className='u-mar-r_2'
                      key={columnsToShow.length}
                      onChange={(event) => handleColumnSelection(event, 'created')}
                      checked={columnsToShow.includes('created')}
                    />
                    Show Date Created
                  </DropdownItem>
                  <DropdownItem>
                    <Checkbox
                      className='u-mar-r_2'
                      key={columnsToShow.length}
                      onChange={(event) => handleColumnSelection(event, 'modified')}
                      checked={columnsToShow.includes('modified')}
                    />
                    Show Date Modified
                  </DropdownItem>
                  <DropdownItem>
                    <Checkbox
                      className='u-mar-r_2'
                      key={columnsToShow.length}
                      onChange={(event) => handleColumnSelection(event, 'questions')}
                      checked={columnsToShow.includes('questions')}
                    />
                    Show Question Count
                  </DropdownItem>
                </Dropdown>
              ]}
              buttonAlign='left'
              inputAlign='right'
              searchFilters={getSearchFilters}
              query={getFolderQuery()}
            >
              <MandarkTable.Column
                heading='Folder Name'
                sortBy='name'
                className={!columnsToShow.includes('name') ? 'u-display_none' : ''}
                content={(folder) => folder.getName()}
                defaultColumnSortDirection='ASCENDING'
              />
              <MandarkTable.Column
                heading='Subjects'
                className={!columnsToShow.includes('subjects') ? 'u-display_none' : ''}
                content={(folder) => {
                  const subjectNames = folder
                    .getQuestionSets()
                    .map((question) => question.getSubject().getName())
                    .toSet()
                    .join(', ');

                  return <div className='folder-table__subjects'>{subjectNames}</div>;
                }}
              />
              <MandarkTable.Column
                heading='Date Created'
                sortBy='inserted_at'
                className={!columnsToShow.includes('created') ? 'u-display_none' : ''}
                content={(folder) => renderMoment(folder.getInsertedAt(), 'dateFormal')}
              />
              <MandarkTable.Column
                defaultSortParam
                heading='Last Modified'
                sortBy='last_question_set_inserted_at'
                className={!columnsToShow.includes('modified') ? 'u-display_none' : ''}
                content={(folder) =>
                  renderMoment(
                    folder.getMeta().getQuestionSetRelationshipUpdatedAt()
                      ? folder.getMeta().getQuestionSetRelationshipUpdatedAt()
                      : folder.getUpdatedAt(),
                    'dateFormal'
                  )
                }
              />
              <MandarkTable.Column
                heading='Questions'
                sortBy='meta.count_of_questions'
                className={!columnsToShow.includes('questions') ? 'u-display_none' : ''}
                content={(folder) => folder.getMeta().getCountOfQuestions()}
              />
              <MandarkTable.Column isActionMenu content={makeDropdownButton} />
            </MandarkTable>
          </div>
          {folderToRename && (
            <RenameFolderModal
              folder={folderToRename}
              handleClose={() => setFolderToRename(null)}
            />
          )}
          {folderToDelete && (
            <DeleteFolderModal
              folder={folderToDelete}
              handleClose={() => setFolderToDelete(null)}
            />
          )}
          {folderToDuplicate && (
            <DuplicateFolderModal
              folder={folderToDuplicate}
              handleClose={() => setFolderToDuplicate(null)}
            />
          )}
          {showNewFolderModal && (
            <NewFolderModal handleClose={() => setShowNewFolderModal(false)} />
          )}
          {showEmptyFolderModal && (
            <EmptyFolderModal
              toggleEmptyFolderModal={toggleEmptyFolderModal}
              handleClose={() => setShowEmptyFolderModal(false)}
            />
          )}
          {renderSmartAssignModal()}
        </div>
      )}
      {showUpgradeModal && (
        <UpgradeSubjectModal
          subject={blockedSubject}
          handleClose={() => {
            setShowUpgradeModal(false);
          }}
        />
      )}
    </>
  );
};

export default Folders;
